import { useEffect, useState, useCallback } from "react";
import { SlidersHorizontal, Search } from "lucide-react";
import api from "@/lib/api";
import OfferCard from "@/components/OfferCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [destination, setDestination] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (destination) params.destination = destination;
      if (minPrice) params.min_price = Number(minPrice);
      if (maxPrice) params.max_price = Number(maxPrice);
      if (dateFilter) params.date = dateFilter;
      const { data } = await api.get("/offers", { params });
      setOffers(data);
    } finally {
      setLoading(false);
    }
  }, [destination, minPrice, maxPrice, dateFilter]);

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const reset = () => {
    setDestination("");
    setMinPrice("");
    setMaxPrice("");
    setDateFilter("");
    setTimeout(load, 0);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <h1 className="font-head text-4xl font-extrabold text-primary mb-2">Nos offres</h1>
      <p className="text-muted-foreground mb-8">Séjours et hôtels — trouvez la formule qui vous ressemble.</p>

      <div className="bg-white rounded-2xl soft-shadow border border-gray-100 p-5 mb-8">
        <div className="flex items-center gap-2 text-primary font-semibold mb-4">
          <SlidersHorizontal className="h-4 w-4" /> Filtres
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <label className="text-xs text-muted-foreground">Destination</label>
            <Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Ex. Dubaï, Paris..." className="rounded-xl" data-testid="filter-destination" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Prix min (FCFA)</label>
            <Input type="number" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} placeholder="0" className="rounded-xl" data-testid="filter-min-price" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Prix max (FCFA)</label>
            <Input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="2000000" className="rounded-xl" data-testid="filter-max-price" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Date de départ</label>
            <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="rounded-xl" data-testid="filter-date" />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={load} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="apply-filters-btn">
            <Search className="mr-2 h-4 w-4" /> Rechercher
          </Button>
          <Button onClick={reset} variant="ghost" className="rounded-full" data-testid="reset-filters-btn">Réinitialiser</Button>
        </div>
      </div>

      {loading ? (
        <div className="min-h-[30vh] flex items-center justify-center">
          <div className="h-10 w-10 rounded-full border-4 border-accent border-t-transparent animate-spin" />
        </div>
      ) : offers.length === 0 ? (
        <p className="text-center text-muted-foreground py-16" data-testid="no-offers">Aucune offre ne correspond à vos critères.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="offers-grid">
          {offers.map((o) => (
            <OfferCard key={o.id} offer={o} />
          ))}
        </div>
      )}
    </div>
  );
}
